import React, { useEffect, useRef, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Building2, Upload, Loader2, Trash2 } from "lucide-react";

const EMPTY = { name: "", manager_name: "", contact_email: "", phone: "", address_line1: "", address_line2: "", town: "", postcode: "", beds: "" };

export function LogoBox({ url, size = "h-24 w-24" }) {
  return (
    <div data-testid="profile-logo-preview" className={`${size} rounded-2xl border border-cream-deep bg-white grid place-items-center overflow-hidden shrink-0`}>
      {url ? <img src={url} alt="Care home logo" className="max-h-full max-w-full object-contain p-2" /> : <Building2 className="h-8 w-8 text-brand/30" />}
    </div>
  );
}

export function CareHomeProfileForm({ getUrl, putUrl, logoUrl, onSaved, allowRemoveLogo = true }) {
  const [form, setForm] = useState(EMPTY);
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get(getUrl).then((r) => {
      const d = r.data;
      setForm({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, d[k] ?? ""])) });
      setLogo(d.logo_url || null);
    }).catch(() => {});
  }, [getUrl]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = { ...form, beds: form.beds ? Number(form.beds) : null, contact_email: form.contact_email || null };
      const { data } = await api.put(putUrl, payload);
      toast.success("Profile saved");
      onSaved?.(data);
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(logoUrl, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo(data.logo_url); toast.success("Logo uploaded"); onSaved?.();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeLogo = async () => {
    try { await api.delete(logoUrl); setLogo(null); toast.success("Logo removed"); onSaved?.(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };

  return (
    <form onSubmit={save} className="space-y-6" data-testid="care-home-profile-form">
      <div className="flex flex-wrap items-center gap-5">
        <LogoBox url={logo} />
        <div className="space-y-2">
          <div className="font-heading font-bold text-brand">Company logo</div>
          <p className="text-xs text-muted-foreground max-w-xs">PNG, JPG, WEBP or SVG up to 5MB. Shown in your dashboard and printed on your Evidence Pack and Training Report PDFs.</p>
          <div className="flex gap-2">
            <input ref={fileRef} data-testid="profile-logo-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} />
            <Button type="button" size="sm" variant="outline" data-testid="profile-logo-upload-btn" disabled={uploading} onClick={() => fileRef.current?.click()} className="rounded-full h-8">
              {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1" />}{logo ? "Replace logo" : "Upload logo"}
            </Button>
            {logo && allowRemoveLogo && <Button type="button" size="sm" variant="ghost" data-testid="profile-logo-remove-btn" onClick={removeLogo} className="rounded-full h-8 text-rag-red"><Trash2 className="h-3.5 w-3.5 mr-1" />Remove</Button>}
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div><Label>Care home name</Label><Input data-testid="profile-name" required value={form.name} onChange={set("name")} /></div>
        <div><Label>Manager / administrator name</Label><Input data-testid="profile-manager-name" value={form.manager_name} onChange={set("manager_name")} /></div>
        <div><Label>Contact email</Label><Input data-testid="profile-email" type="email" value={form.contact_email} onChange={set("contact_email")} /></div>
        <div><Label>Telephone</Label><Input data-testid="profile-phone" value={form.phone} onChange={set("phone")} placeholder="01243 …" /></div>
        <div className="md:col-span-2"><Label>Address line 1</Label><Input data-testid="profile-address1" value={form.address_line1} onChange={set("address_line1")} /></div>
        <div className="md:col-span-2"><Label>Address line 2</Label><Input data-testid="profile-address2" value={form.address_line2} onChange={set("address_line2")} /></div>
        <div><Label>Town / city</Label><Input data-testid="profile-town" value={form.town} onChange={set("town")} /></div>
        <div className="grid grid-cols-2 gap-4">
          <div><Label>Postcode</Label><Input data-testid="profile-postcode" value={form.postcode} onChange={set("postcode")} /></div>
          <div><Label>Registered beds</Label><Input data-testid="profile-beds" type="number" min="1" max="2000" value={form.beds} onChange={set("beds")} /></div>
        </div>
      </div>
      <Button type="submit" data-testid="profile-save-btn" disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-white h-10 px-6">{saving ? "Saving…" : "Save profile"}</Button>
    </form>
  );
}
